import React from 'react';
import RedesignedMentionsPing from './redesigned_mentions_ping';
import routes from 'js/core_pre_webpack/routes';
import commaize from 'js/utils/commaize';
import { keptAlivePost } from 'js/utils/kept_alive_post';
import trackClick from 'js/utils/trackClick';
import indefinite from 'indefinite';
import Modal from 'js/components/modals/Modal';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';
import { ImpressionEntity } from 'js/utils/impressions/watchImpressions';
import $image_url from '../../utils/image_url';
import styled from 'astroturf/react';
import { Props as OnsitePingProps } from 'js/onsite_pings/react/onsite_ping';

const AnchorContainer = styled.div`
  width: 368px;
  max-width: 100%;
  padding: 0;
  white-space: nowrap;
  display: flex;
  justify-content: center;

  @media screen and (min-width: 950px) {
    padding: 10px 20px;
  }
`;

const Anchor = styled.a`
  width: 100%;
  border-radius: 4px !important;
  padding: 17px 32px;

  background: #0849b8;
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.2);

  font-family: Roboto;
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 22px;
  text-align: center;
  color: #ffffff;

  &:hover,
  &:active {
    text-decoration: none;
    color: #ffffff;
  }
`;

// Use the shared impressions pipeline so every mentions ping lands in the same table.
const Takeover = styled(ImpressionWatcher)`
  @media screen and (min-width: 950px) {
    padding: 0 100px;
  }

  display: flex;
  flex-direction: column;
  align-items: center;
  max-height: 100%;
  justify-content: space-evenly;
  max-width: calc(100vw - 32px * 2 - 20px);

  font-family: roboto;
  text-align: center;

  h1 {
    font-weight: 300;
    font-size: 29px;
    line-height: 41px;
    margin: 0;
    font-weight: 500;

    @media screen and (min-width: 950px) {
      font-size: 32px;
      line-height: 41.6px;
      margin: 10px 0;
    }
  }

  p {
    margin: 12px 0;
    font-weight: 400;
    font-size: 18px;
    line-height: 26px;
    width: 605px;
    max-width: calc(100% - 20px);

    @media screen and (min-width: 950px) {
      font-size: 20px;
      line-height: 28px;
    }
  }
`;

const TakeoverImage = styled.img`
  width: 120px;
  max-width: 100%;

  @media screen and (min-width: 950px) {
    width: 218px;
  }
`;

const CtaColumn = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  justify-content: center;
  max-width: 100%;

  font-size: 16px;
  line-height: 22px;
`;

const NoThanksButton = styled.button`
  margin: 10px 12px;
  padding: 0;
  background: none;
  border: none;
  color: #0849b8;
  font-weight: 500;
`;

interface Props extends OnsitePingProps {
  mentionedName: string;
  researchInterestName: string;
  type: string;
  mentionsMilestoneOrdinal: string;
  mentionsMilestone: number;
  userFirstName: string;
  combinedMentionId?: number;
}

type State = {
  open: boolean;
};

class MentionsMilestonePing extends RedesignedMentionsPing<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      open: true,
    };
  }

  render() {
    if (!this.state.open) {
      return null;
    } else {
      return this.renderTakeoverPing();
    }
  }

  renderTakeoverPing() {
    return (
      <Modal onClose={() => this.dismissPing()}>
        <Takeover
          source={this.trackingSources().impression}
          entity_id={this.trackingEntityId() ?? undefined}
          entity_type={
            this.trackingEntityId() != null
              ? ImpressionEntity.Mention
              : undefined
          }
          extra={this.trackingExtra()}
        >
          <TakeoverImage
            src={$image_url('page_celebration_quote.svg')}
            alt="Academia Premium"
          />
          <h1>{this.getSubjectText()}</h1>
          <p>{this.getBody()}</p>
          <CtaColumn>
            {this.renderButton()}
            <NoThanksButton onClick={() => this.dismissPing()}>
              No Thanks
            </NoThanksButton>
          </CtaColumn>
        </Takeover>
      </Modal>
    );
  }

  dismissPing(): void {
    this.setState({ open: false });
    this.recordDismissal();
  }

  recordDismissal(): void {
    trackClick({
      source: this.trackingSources().dismiss,
      entityId: this.trackingEntityId(),
      extra: this.trackingExtra(),
    });
    const url = routes.record_ping_dismissal_api_v0_onsite_pings_path({
      subdomain_param: 'api',
    });
    keptAlivePost(url, {
      processData: false,
      data: { ping_name: this.props.pingName },
    });
  }

  getBody() {
    return this.props.type === 'paper'
      ? this.getPaperText()
      : this.getAuthorText();
  }

  getPaperText() {
    const { researchInterestName, mentionedName } = this.props;
    const article = indefinite(researchInterestName, { articleOnly: true });

    return (
      <div className="text">
        The name <strong>{mentionedName}</strong> is mentioned in {article}{' '}
        <strong>{researchInterestName}</strong> paper uploaded to Academia.
      </div>
    );
  }

  getAuthorText() {
    const { researchInterestName, mentionedName } = this.props;
    const article = indefinite(researchInterestName, { articleOnly: true });

    return (
      <div className="text">
        The name <strong>{mentionedName}</strong> is mentioned by {article}{' '}
        <strong>{researchInterestName}</strong> researcher in a paper uploaded
        to Academia.
      </div>
    );
  }

  renderButton() {
    return (
      <AnchorContainer>
        <Anchor
          href={this.getUpgradeURL()}
          onClick={() => this.recordClickthrough()}
        >
          {this.getButtonText()}
        </Anchor>
      </AnchorContainer>
    );
  }

  getUpgradeURL() {
    return routes.upgrade_url({
      feature: 'searchm',
      trigger: 'mentions-view-download-milestone-onsite-ping',
      after_upgrade_path: encodeURIComponent(routes.mentions_path()),
    });
  }

  recordClickthrough(): void {
    trackClick({
      source: this.trackingSources().ctaClick,
      entityId: this.trackingEntityId(),
      extra: this.trackingExtra(),
    });
    const url = routes.record_ping_clickthrough_api_v0_onsite_pings_path({
      subdomain_param: 'api',
    });
    keptAlivePost(url, {
      processData: false,
      data: { ping_name: this.props.pingName },
    });
  }

  getSubjectText() {
    const { mentionsMilestone, mentionsMilestoneOrdinal, userFirstName } =
      this.props;
    // Convert to 1,234th
    const mentionsCount = `${commaize(
      mentionsMilestone,
    )}${mentionsMilestoneOrdinal}`;
    return `Congratulations on your ${mentionsCount} mention, ${userFirstName}!`;
  }

  // Kept as its own override rather than inherited: the milestone takeover is outside
  // growth_2026_q3_new_citation_pings_v2, so its label never switches to citation wording.
  getButtonText() {
    return 'View your mentions ▸';
  }

  renderUserInfo() {
    return (
      <div className="user-info">
        <div className="celebratory-title">
          <div>{this.getSubjectText()}</div>
        </div>
      </div>
    );
  }
}

export default MentionsMilestonePing;
